import { MarkerClusterer } from "@googlemaps/markerclusterer";

async function fetchAllMapLocations() {
  const { storefrontApiUrl, storefrontAccessToken } = window.mapLocationsConfig;

  const query = `
    query StorefrontMapLocations($cursor: String) {
      metaobjects(type: "map_location", first: 250, after: $cursor) {
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          handle
          name: field(key: "name") { value }
          latitude: field(key: "latitude") { value }
          longitude: field(key: "longitude") { value }
          address: field(key: "address") { value }
          postcode: field(key: "postcode") { value }
        }
      }
    }
  `;

  let allNodes = [];
  let cursor = null;
  let hasNextPage = true;

  while (hasNextPage) {
    const response = await fetch(storefrontApiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": storefrontAccessToken,
      },
      body: JSON.stringify({
        query,
        variables: { cursor },
      }),
    });

    const json = await response.json();

    if (json.errors) {
      console.error("Storefront API errors:", json.errors);
      throw new Error("Error from Storefront API");
    }

    const { pageInfo, nodes } = json.data.metaobjects;
    allNodes = allNodes.concat(nodes);
    hasNextPage = pageInfo.hasNextPage;
    cursor = pageInfo.endCursor;
  }

  // Convert to simple array for Google Maps
  return allNodes.map((node) => ({
    handle: node.handle,
    name: node.name?.value,
    lat: parseFloat(node.latitude?.value),
    lng: parseFloat(node.longitude?.value),
    address: node.address?.value,
    postcode: node.postcode?.value,
  }));
}

let hasInitializedMap = false;

function createCustomPin(loc) {
  const pinWrap = document.createElement("div");
  pinWrap.style.width = "30px";
  pinWrap.style.height = "30px";
  pinWrap.style.borderRadius = "50%";
  pinWrap.style.background = "#fff";
  pinWrap.style.border = "1px solid #1b355d";
  pinWrap.style.boxShadow = "0 2px 8px rgba(0,0,0,0.28)";
  pinWrap.style.display = "flex";
  pinWrap.style.alignItems = "center";
  pinWrap.style.justifyContent = "center";
  pinWrap.style.overflow = "hidden";

  const img = document.createElement("img");
  img.src = window.mapLocationsConfig?.pinImageUrl || "";
  img.alt = loc.name || "Location";
  img.style.width = "24px";
  img.style.height = "24px";
  img.style.objectFit = "contain";
  img.style.display = "block";

  pinWrap.appendChild(img);
  return pinWrap;
}

async function initMapWithLocations(mapInstance) {
  const locations = await fetchAllMapLocations();
  await google.maps.importLibrary("maps");
  const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");
  const infoWindow = new google.maps.InfoWindow({
    content: "",
    disableAutoPan: true,
  });
  const bounds = new google.maps.LatLngBounds();
  const markers = locations
    .filter((loc) => isFinite(loc.lat) && isFinite(loc.lng))
    .map((loc) => {
      const formattedAddress = (loc.address || "")
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean)
        .join("<br>");

      const marker = new AdvancedMarkerElement({
        position: { lat: loc.lat, lng: loc.lng },
        title: loc.name,
        content: createCustomPin(loc),
        gmpClickable: true
      });

      marker.addEventListener("gmp-click", () => {
        const content = `
          <div style="overflow:hidden;max-height:none;line-height:1.4;padding: 0 12px 12px 0;">
            <strong>${loc.name || "Location"}</strong><br>
            ${formattedAddress}
          </div>
        `;
        infoWindow.setContent(content.trim());
        infoWindow.open({
          anchor: marker,
          map: mapInstance,
        });

        // Google wraps InfoWindow content in a scrolling container by default.
        requestAnimationFrame(() => {
          const contentContainer = document.querySelector(".gm-style .gm-style-iw-d");
          if (contentContainer) {
            contentContainer.style.overflow = "hidden";
            contentContainer.style.maxHeight = "none";
          }
        });
      });

      bounds.extend({ lat: loc.lat, lng: loc.lng });
      return marker;
    });

  new MarkerClusterer({ markers, map: mapInstance });

  if (markers.length === 1) {
    mapInstance.setCenter(bounds.getCenter());
    mapInstance.setZoom(12);
  } else if (markers.length > 1) {
    mapInstance.fitBounds(bounds);
  }
}

// Call initMapWithLocations once your Google Map is initialized
// For example, if you use a global init callback:
window.initMap = async function () {
  if (hasInitializedMap) return;

  if (!window.google?.maps) {
    console.error("Google Maps API is not available yet.");
    return;
  }

  const mapElementId = window.mapLocationsConfig?.mapElementId;
  const mapElement = mapElementId
    ? document.getElementById(mapElementId)
    : document.querySelector("[data-map-element]");

  if (!mapElement) {
    console.warn("Map container was not found on the page.");
    return;
  }

  if (mapElement.clientHeight === 0 || mapElement.clientWidth === 0) {
    console.warn("Map container is present but has no visible size.");
    return;
  }

  hasInitializedMap = true;

  const googleMapId = window.mapLocationsConfig?.googleMapId;

  const map = new google.maps.Map(mapElement, {
    center: { lat: 0, lng: 0 },
    zoom: 2,
    mapId: googleMapId,
  });

  try {
    await initMapWithLocations(map);
  } catch (error) {
    console.error("Unable to initialize map locations:", error);
  }
};